import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Truck } from "lucide-react";
import { useCart, FREE_SHIPPING_THRESHOLD } from "../context/CartContext";
import { useState } from "react";

export const Layout = () => {
  const { count, freeShipEligibleSubtotal } = useCart();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const isAdmin = loc.pathname.startsWith("/admin");

  if (isAdmin) {
    return <Outlet />;
  }

  const eligible = freeShipEligibleSubtotal >= FREE_SHIPPING_THRESHOLD;
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - freeShipEligibleSubtotal);
  let bannerText;
  if (eligible) bannerText = "🎉 You've unlocked FREE shipping on this order!";
  else if (freeShipEligibleSubtotal > 0) bannerText = `You're only $${remaining.toFixed(2)} away from FREE shipping!`;
  else bannerText = `Free shipping on all orders over $${FREE_SHIPPING_THRESHOLD}`;

  return (
    <div className="min-h-screen flex flex-col" data-testid="public-layout">
      <div
        className="bg-[var(--navy)] text-[var(--bone)] text-center py-2 px-4 flex items-center justify-center gap-2 font-mono-ui text-[10px] sm:text-[11px] tracking-[0.2em] uppercase"
        data-testid="free-shipping-banner"
      >
        <Truck className="w-3.5 h-3.5 text-[var(--gold)]" />
        <span className={eligible ? "text-[var(--gold)]" : ""}>{bannerText}</span>
      </div>
      <div className="flag-divider" />
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[rgba(244,241,234,0.85)] border-b border-[rgba(27,42,65,0.1)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-home-link" className="flex items-center gap-3">
            <div className="w-10 h-10 grid place-items-center rounded-sm bg-[var(--navy)] text-[var(--bone)] font-serif-display text-lg">G</div>
            <div className="leading-tight">
              <div className="font-serif-display text-2xl text-[var(--navy)]">Papa G and MawMaw&apos;s Shop</div>
              <div className="font-mono-ui text-[10px] tracking-[0.25em] text-[var(--ink-soft)]">EST. 2019 · MADE WITH LOVE</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8 font-mono-ui text-xs tracking-[0.25em] uppercase">
            <NavLink to="/" end data-testid="nav-home" className={({isActive}) => isActive ? "text-[var(--crimson)]" : "text-[var(--navy)] hover:text-[var(--crimson)]"}>Home</NavLink>
            <NavLink to="/shop" data-testid="nav-shop" className={({isActive}) => isActive ? "text-[var(--crimson)]" : "text-[var(--navy)] hover:text-[var(--crimson)]"}>Shop</NavLink>
            <NavLink to="/shop?category=tshirt" data-testid="nav-tshirts" className="text-[var(--navy)] hover:text-[var(--crimson)]">T-Shirts</NavLink>
            <NavLink to="/shop?category=tumbler" data-testid="nav-tumblers" className="text-[var(--navy)] hover:text-[var(--crimson)]">Tumblers</NavLink>
            <NavLink to="/shop?category=handmade" data-testid="nav-handmade" className="text-[var(--gold)] hover:text-[var(--crimson)]">MawMaw&apos;s Handmade</NavLink>
            <NavLink to="/track" data-testid="nav-track" className="text-[var(--navy)] hover:text-[var(--crimson)]">Track Order</NavLink>
            <NavLink to="/contact" data-testid="nav-contact" className={({isActive}) => isActive ? "text-[var(--crimson)]" : "text-[var(--navy)] hover:text-[var(--crimson)]"}>Contact</NavLink>
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/cart" data-testid="nav-cart-button" className="relative p-2 rounded-sm hover:bg-[var(--bone-2)] transition">
              <ShoppingBag className="w-5 h-5 text-[var(--navy)]" />
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[var(--crimson)] text-[var(--bone)] text-[10px] grid place-items-center font-bold">{count}</span>
              )}
            </Link>
            <button data-testid="nav-mobile-toggle" className="md:hidden p-2" onClick={() => setOpen(!open)}>
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-[rgba(27,42,65,0.1)] bg-[var(--bone)]">
            <div className="px-6 py-4 flex flex-col gap-3 font-mono-ui text-xs tracking-[0.2em] uppercase">
              <Link to="/" onClick={() => setOpen(false)}>Home</Link>
              <Link to="/shop" onClick={() => setOpen(false)}>Shop</Link>
              <Link to="/shop?category=tshirt" onClick={() => setOpen(false)}>T-Shirts</Link>
              <Link to="/shop?category=tumbler" onClick={() => setOpen(false)}>Tumblers</Link>
              <Link to="/shop?category=handmade" onClick={() => setOpen(false)}>MawMaw&apos;s Handmade</Link>
              <Link to="/track" onClick={() => setOpen(false)}>Track Order</Link>
              <Link to="/contact" onClick={() => setOpen(false)}>Contact</Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 relative">
        <Outlet />
      </main>

      <footer className="mt-24 bg-[var(--navy)] text-[var(--bone)] relative overflow-hidden">
        <div className="flag-divider" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="font-serif-display text-3xl mb-3">Papa G and MawMaw&apos;s Shop</div>
            <p className="text-[var(--bone-2)]/80 max-w-md leading-relaxed">
              A little family shop where MawMaw gets to be able to work from home.
              Every order is printed, packed and shipped with care and appreciation.
            </p>
          </div>
          <div>
            <div className="font-mono-ui text-[10px] tracking-[0.3em] text-[var(--gold)] mb-3">SHOP</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/shop?category=tshirt">T-Shirts</Link></li>
              <li><Link to="/shop?category=tumbler">Tumblers</Link></li>
              <li><Link to="/shop">All Goods</Link></li>
              <li><Link to="/contact" data-testid="footer-contact-link">Contact / Custom Request</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-mono-ui text-[10px] tracking-[0.3em] text-[var(--gold)] mb-3">THE SHOP</div>
            <ul className="space-y-2 text-sm">
              <li>Mon–Sat · 9–6 EST</li>
              <li>contact@papagshop.com</li>
              <li><Link to="/admin" data-testid="footer-admin-link">Shop Sign-in</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-6 text-center font-mono-ui text-[10px] tracking-[0.3em] text-[var(--bone-2)]/60">
          © {new Date().getFullYear()} PAPA G&apos;S SHOP · A FAMILY BUSINESS
        </div>
      </footer>
    </div>
  );
};
